import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import JudgeDrawer from "../components/JudgeDrawer";
import CompositeJudgeMapPanel from "../components/CompositeJudgeMapPanel";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useSortedRows } from "../hooks/useSortedRows";

const TAB_ROSTER = "roster";
const TAB_COMPOSITE = "composite";

// #732 — Judges section: list of the active show's judges (score-api
// GET /judge?showId=, seeded onto the daysheet by foxbox #729 and reconciled
// into score-judge by #730), with a per-judge drawer for signature capture.
// Same list+drawer pattern as HorseManager/PeopleManager (#507) minus search
// (shows have a small handful of judges, not hundreds).
export default function JudgeManager() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.judges"));
  const scoreApi = useScoreApi();
  // #2195 — `loading` (aliased loadingShow) covers the show-LIST fetch: while
  // it's still in flight activeShowId is null too, same shape as everywhere
  // else in scoreadmin-fe, and rendering "no show" text off a null id that
  // simply hasn't resolved yet told the operator there was no show before we
  // knew that.
  const { activeShowId, loading: loadingShow } = useActiveShow();

  const [judges, setJudges] = useState([]);
  // #3186 -- `loading` is true while a fetch is in flight AND until the data
  // for the CURRENT show has landed once. A flag only flipped inside the fetch
  // was false for one frame after a show change, so the previous show's list
  // (or the empty text) flashed before the skeleton.
  const [inFlight, setInFlight] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const loading = inFlight || (Boolean(activeShowId) && loadedFor !== activeShowId);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // #1742 — new tab container. The roster table (below) stays the first tab,
  // unchanged; "Composite judges" is the new panel that maps one packed
  // roster row to its two real judges (card #1342's mapping entity).
  // #3570 -- the Pre-show dashboard's Fix links: ?judge=<id> opens that judge's
  // drawer, ?tab=composite opens the Composite judges tab (ADR 0004).
  const [searchParams] = useSearchParams();
  const deepJudgeId = searchParams.get("judge");
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === TAB_COMPOSITE ? TAB_COMPOSITE : TAB_ROSTER
  );
  const openedDeepJudge = useRef(null);
  useEffect(() => {
    // Once per link: closing the drawer must not reopen it on the next reload of the list.
    if (!deepJudgeId || openedDeepJudge.current === deepJudgeId) return;
    if (judges.some((j) => j.id === deepJudgeId)) {
      openedDeepJudge.current = deepJudgeId;
      setSelectedId(deepJudgeId);
    }
  }, [deepJudgeId, judges]);

  const loadJudges = useCallback(async () => {
    if (!activeShowId) {
      setJudges([]);
      return;
    }
    try {
      setInFlight(true);
      setError(null);
      const list = unwrap(await scoreApi.judges.listJudges(activeShowId));
      setJudges(list);
    } catch (err) {
      setError(err);
    } finally {
      setInFlight(false);
      setLoadedFor(activeShowId);
    }
  }, [scoreApi, activeShowId]);

  useEffect(() => {
    loadJudges();
  }, [loadJudges]);

  const selected = judges.find((j) => j.id === selectedId) || null;

  // #870 — sortable columns, same pattern as HorseManager/PeopleManager (#557).
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    judges,
    {
      name: (j) => userDisplayName(j),
      role: (j) => j.role,
      country: (j) => j.country,
    },
    "name",
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("judges.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("judges.loadError")}
        </Alert>
      )}

      {/* #1742 — textColor="inherit" + indicatorColor="secondary", same fix as
          RideEditor's judge tabs (#581/#587): the default selected-tab color
          is theme primary.main (brand darkGrey #363636), ~1.5:1 contrast on a
          dark background.paper and effectively unreadable. */}
      <Tabs
        value={activeTab}
        onChange={(_e, value) => setActiveTab(value)}
        textColor="inherit"
        indicatorColor="secondary"
        sx={{ mb: 2 }}
      >
        <Tab value={TAB_ROSTER} label={t("judges.tabs.roster")} />
        <Tab value={TAB_COMPOSITE} label={t("judges.tabs.composite")} />
      </Tabs>

      {activeTab === TAB_ROSTER ? (
        !activeShowId ? (
          loadingShow ? (
            <TableSkeleton rows={4} columns={5} />
          ) : (
            <Typography color="text.secondary">{t("judges.noShow")}</Typography>
          )
        ) : loading ? (
          <TableSkeleton rows={4} columns={5} />
        ) : judges.length === 0 ? (
          <Typography color="text.secondary">{t("judges.empty")}</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    { key: "name", labelKey: "judges.columns.name" },
                    { key: "role", labelKey: "judges.columns.role" },
                    { key: "country", labelKey: "judges.columns.country" },
                  ].map((col) => (
                    <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : "asc"}
                        onClick={() => toggleSort(col.key)}
                      >
                        {t(col.labelKey)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell>{t("judges.columns.usefId")}</TableCell>
                  <TableCell>{t("judges.columns.signature")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((judge) => (
                  <TableRow
                    key={judge.id}
                    hover
                    onClick={() => setSelectedId(judge.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{userDisplayName(judge) || "-"}</TableCell>
                    <TableCell>{judge.role || "-"}</TableCell>
                    <TableCell>{judge.country || "-"}</TableCell>
                    <TableCell>
                      {judge.centralJudge?.usefId ? (
                        judge.centralJudge.usefId
                      ) : (
                        <Chip size="small" color="warning" variant="outlined" label={t("judges.usefIdMissing")} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          judge.signaturePresent
                            ? t("judges.drawer.signatureOnFile")
                            : t("judges.drawer.signatureMissing")
                        }
                        color={judge.signaturePresent ? "success" : "default"}
                        variant={judge.signaturePresent ? "filled" : "outlined"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <CompositeJudgeMapPanel
          judges={judges}
          judgesLoading={loading}
          judgesError={error}
          activeShowId={activeShowId}
        />
      )}

      <JudgeDrawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        judge={selected}
        onSignatureChanged={loadJudges}
        onUsefIdChanged={loadJudges}
      />
    </Box>
  );
}
